import { useMemo, useState } from "preact/hooks";
import type { CrmData } from "../../types";
import { InvoiceStatusBadge } from "../ui/Badge";
import { Avatar } from "../ui/Avatar";
import { EmptyState } from "../ui/States";
import { collectedAmount, computeInvoiceTotals, effectiveInvoiceStatus, outstandingBalance, overdueAmount } from "../../lib/billing";
import { formatDate, money } from "../../lib/format";

export function InvoicesPage({
  data,
  now,
  onOpenInvoice,
  onNewInvoice,
}: {
  data: CrmData;
  now: Date;
  onOpenInvoice: (invoiceId: string) => void;
  onNewInvoice: () => void;
}) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const summary = useMemo(
    () => ({
      outstanding: outstandingBalance(data.invoices),
      overdue: overdueAmount(data.invoices, now),
      collected: collectedAmount(data.invoices),
    }),
    [data.invoices, now]
  );

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return data.invoices
      .map((inv) => {
        const company = data.companies.find((c) => c.id === inv.companyId);
        const opportunity = inv.opportunityId ? data.opportunities.find((o) => o.id === inv.opportunityId) : undefined;
        return { inv, company, opportunity, status: effectiveInvoiceStatus(inv, now), total: computeInvoiceTotals(inv).total };
      })
      .filter((r) => {
        if (statusFilter && r.status !== statusFilter) return false;
        if (term && !r.inv.invoiceNumber.toLowerCase().includes(term) && !(r.company?.name.toLowerCase().includes(term))) return false;
        return true;
      })
      .sort((a, b) => new Date(b.inv.issueDate).getTime() - new Date(a.inv.issueDate).getTime());
  }, [data.invoices, data.companies, data.opportunities, search, statusFilter, now]);

  return (
    <div class="page">
      <div class="page__header">
        <div>
          <h1 class="page__title">Billing</h1>
          <p class="page__subtitle">{data.invoices.length} invoices across your accounts</p>
        </div>
        <button type="button" class="btn btn--primary" data-ai-id="create-invoice" onClick={onNewInvoice}>
          + New Invoice
        </button>
      </div>

      <div class="summary-bar">
        <div class="summary-chip">
          <div class="summary-chip__label">Outstanding balance</div>
          <div class="summary-chip__value">{money(summary.outstanding)}</div>
          <div class="summary-chip__meta">awaiting payment</div>
        </div>
        <div class="summary-chip">
          <div class="summary-chip__label">
            <span class="warn-chip__dot" style={{ background: "var(--dot-red)" }} />
            Overdue
          </div>
          <div class="summary-chip__value">{money(summary.overdue)}</div>
          <div class="summary-chip__meta">past due date</div>
        </div>
        <div class="summary-chip">
          <div class="summary-chip__label">Collected</div>
          <div class="summary-chip__value">{money(summary.collected)}</div>
          <div class="summary-chip__meta">total payments received</div>
        </div>
      </div>

      <div class="filters-bar">
        <input
          class="filters-bar__search"
          type="search"
          placeholder="Search invoice or company…"
          value={search}
          data-ai-id="search"
          onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
        />
        <select value={statusFilter} data-ai-id="filter-status" onChange={(e) => setStatusFilter((e.target as HTMLSelectElement).value)}>
          <option value="">All statuses</option>
          <option value="Draft">Draft</option>
          <option value="Sent">Sent</option>
          <option value="Partially Paid">Partially Paid</option>
          <option value="Overdue">Overdue</option>
          <option value="Paid">Paid</option>
          <option value="Void">Void</option>
        </select>
      </div>

      {rows.length === 0 ? (
        <EmptyState title="No invoices found" description="Try a different search term or filter, or create a new invoice." />
      ) : (
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Company</th>
                <th>Deal</th>
                <th>Issued</th>
                <th>Due</th>
                <th>Total</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.inv.id}
                  class="table-row-clickable"
                  data-ai-id={`open-invoice-${r.inv.id}`}
                  onClick={() => onOpenInvoice(r.inv.id)}
                >
                  <td class="num" style={{ fontWeight: 550 }}>
                    {r.inv.invoiceNumber}
                  </td>
                  <td>
                    <span class="td-company">
                      <Avatar name={r.company?.name ?? "?"} size="sm" />
                      {r.company?.name ?? "—"}
                    </span>
                  </td>
                  <td class="muted" style={{ maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {r.opportunity?.name ?? "—"}
                  </td>
                  <td>{formatDate(r.inv.issueDate)}</td>
                  <td>{formatDate(r.inv.dueDate)}</td>
                  <td class="td-num">{money(r.total)}</td>
                  <td>
                    <InvoiceStatusBadge status={r.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
